import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ApolloError } from "@apollo/client";
import { Box, Divider, Stack, TextField, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import debounce from "lodash/debounce";

import ControlTable from "../../../common/components/stream/ControlTable";
import FocusControls from "../../../common/components/stream/FocusControls";
import FocusRectOverlay from "../../../common/components/stream/FocusRectOverlay";
import confirmDialogStyleOptions from "../../../common/helpers/confirmDialogParams";
import createS3KeyForZone from "../../../common/helpers/createS3KeyForZone";
import {
  DEFAULT_FOCUS_RECT,
  sliderToFocusDistance,
  isDefaultRect,
  rectToPolygon,
} from "../../../common/helpers/focusUtils";
import { validateZoneName } from "../../../common/helpers/validateZoneName";
import useBeforeUnload from "../../../common/hooks/useBeforeUnload";
import { usePTController } from "../../../common/hooks/usePTController";
import StyledLoadingButton from "../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import { extractDeviceNameFromId } from "../../../common/utils/getEntityName";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import HLSStreamPlayer from "../../data-hub/components/liveview/components/cameraView/HLSStreamPlayer";
import { useSyncShadow } from "../../model-manager/hooks/useSyncShadow";
import { IDeviceTableRow } from "../hooks/useDeviceTableRows";
import { useCreateAnnotation } from "../hooks/useCreateAnnotation";
import { useCreateZone } from "./hooks/useCreateZone";
import { ICreateZoneValidationState } from "./hooks/useZoneFormValidation";
import mqttPublish from "../../../common/helpers/mqttPublish";
import { DEFAULT_MEDIA_HEIGHT } from "../../../common/variables/common";
import MainPaperWrapper from "../../../common/components/item/MainPaperWrapper";
import ResponsiveSplitContainer from "../../../common/components/item/ResponsiveSplitContainer";
import { AnnotationType } from "../../../API";

const CreateZoneContent = (): JSX.Element => {
  const confirm = useConfirm();
  const { state } = useLocation();
  const navigate = useNavigate();

  const { syncShadowsForNode } = useSyncShadow();

  const device = state?.deviceDatum as IDeviceTableRow;
  const { deviceId = "" } = useParams<{ deviceId: string }>();

  const { requestDeviceImage, setFocus } = mqttPublish();
  const { createZone, loading } = useCreateZone();
  const { createAnnotation } = useCreateAnnotation();

  const [zoneName, setZoneName] = useState("");
  const [focusMode, setFocusMode] = useState("auto");
  const [focusDistance, setFocusDistance] = useState("50");
  const [focusRect, setFocusRect] = useState<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  } | null>(null);

  const deviceName = device?.name;

  // We determine the serviceId by looking at the first model assigned to the device.
  // This serviceId is used when pausing and resuming models when editing a zone/moving pt
  // This will lead to error if a device has multiple models
  // But typically ogi's only have one model running so it is fine for PROD but buggy for testing on DEV
  const serviceId = device?.models[0]?.serviceId;
  const nodeId = device?.node.id;

  const s3Keyframe = createS3KeyForZone(nodeId, zoneName, deviceName);

  const {
    pan,
    tilt,
    zoom,
    disabled,
    movePtUp,
    movePtDown,
    movePtLeft,
    movePtRight,
    handleGoToPt,
    handlePanInputChange,
    handleTiltInputChange,
    handleZoomInputChange,
    validationState,
    setValidationState,
    validateCreateZoneForm,
  } = usePTController({
    deviceId,
    deviceName: deviceName,
    nodeId: device?.node.id,
    deviceData: device?.deviceData,
    currentPosition: device?.currentPosition,
  });

  const debouncedSendFocus = useMemo(
    () =>
      debounce(
        (
          mode: string,
          rect: { x1: number; y1: number; x2: number; y2: number } | null,
          distance: string
        ) => {
          const { extractedDeviceName } = extractDeviceNameFromId(
            device?.rowId ?? deviceId
          );

          setFocus({
            deviceId: extractedDeviceName,
            nodeId,
            focusMode: mode as "auto" | "manual",
            focusRect: mode === "auto" ? rect ?? DEFAULT_FOCUS_RECT : undefined,
            focusDistance:
              mode === "manual"
                ? sliderToFocusDistance(parseInt(distance))
                : undefined,
          });
        },
        500
      ),
    [nodeId, deviceId]
  );

  const saveZone = (): void => {
    const isValid = validateCreateZoneForm(zoneName, pan, tilt, zoom);

    if (!isValid) {
      return;
    }

    const deviceName = device?.name.replace("DE#", "");

    if (!deviceName) {
      errorNotification("Error with device name. Please try again.");

      return;
    }

    const validName = validateZoneName(zoneName);

    if (!validName) {
      return;
    }

    createZone({
      deviceId: device.rowId ?? "TODO",
      name: zoneName,
      pan: String(pan),
      tilt: String(tilt),
      zoom: String(zoom),
      status: true,
      shouldNotify: true,
      serviceId: serviceId ?? "TODO",
      nodeId: nodeId,
      keyFrame: s3Keyframe,
      focusMode,
      focusDistance,
    })
      .then(async (response): Promise<void> => {
        if (response?.data) {
          // save image to s3
          saveImage();

          // Create AUTOFOCUS_RECT annotation if auto mode with custom rect
          const newZoneId = response.data.addZone?.id;

          if (
            focusMode === "auto" &&
            focusRect &&
            !isDefaultRect(focusRect) &&
            newZoneId
          ) {
            try {
              await createAnnotation({
                name: "Focus Area",
                polygon: JSON.stringify({
                  annotations: rectToPolygon(focusRect),
                }),
                zoneId: newZoneId,
                serviceId: serviceId ?? "TODO",
                deviceId: device.rowId ?? "TODO",
                nodeId: nodeId,
                annotationType: AnnotationType.AUTOFOCUS_RECT,
              });
            } catch (error) {
              console.error("Failed to create autofocus annotation:", error);
            }
          }

          successNotification(`${zoneName} has been created successfully`);

          navigate(`/device/${encodeURIComponent(deviceId)}`);
        }

        if (response.errors) {
          errorNotification();
        }
      })
      .catch((error): void => {
        if (
          (error as ApolloError)?.message.startsWith(
            "The conditional request failed"
          )
        ) {
          errorNotification("Zone name already exists");

          return;
        }

        errorNotification();

        console.error(error);
      })
      .finally((): void => {
        syncShadowsForNode(nodeId);
      });
  };

  const saveImage = (): void => {
    const { extractedDeviceName } = extractDeviceNameFromId(
      device?.rowId ?? deviceId
    );

    requestDeviceImage({
      deviceId: extractedDeviceName,
      nodeId,
      s3Key: s3Keyframe,
      errorMessage: "Something went wrong when saving image to S3",
    }).catch((): void => {
      /* handled in helper */
    });
  };

  const handleZoneNameInputChange = (zoneName?: string): void => {
    setValidationState(
      (prev): ICreateZoneValidationState => ({
        ...prev,
        zoneName: {
          hasError: false,
          errorMessage: "",
        },
      })
    );

    setZoneName(zoneName ?? "");
  };

  const confirmCancel = (): void => {
    confirm({
      title: "Are you sure you want to cancel Zone creation?",
      confirmationText: "Cancel",
      cancellationText: "Back",
      ...confirmDialogStyleOptions,
      confirmationButtonProps: {
        ...confirmDialogStyleOptions.confirmationButtonProps,
        disabled: loading,
        color: "primary",
      },
    }).then(async (): Promise<void> => {
      navigate(`/device/${encodeURIComponent(deviceId)}`);
    });
  };

  // TODO: implement another way to handle url change
  // const confirmUrlChange = (path: string): void => {
  //   confirm({
  //     title: "If you leave, the new zone won't be saved.",
  //     content: "Go back to save the changes.",
  //     confirmationText: "Leave",
  //     cancellationText: "Cancel",
  //     ...confirmDialogStyleOptions,
  //     confirmationButtonProps: {
  //       ...confirmDialogStyleOptions.confirmationButtonProps,
  //       color: "primary",
  //     },
  //   }).then((): void => {
  //     resetValidationState();
  //     navigate(path);
  //   });
  // };

  // useUrlChange(confirmUrlChange);

  useBeforeUnload();

  return (
    <>
      <MainPaperWrapper>
        <ResponsiveSplitContainer gap="1.5em">
          <Box>
            <Stack direction="column" spacing={2}>
              <TextField
                margin="dense"
                label="Zone name"
                value={zoneName}
                error={validationState.zoneName.hasError}
                helperText={validationState.zoneName.errorMessage}
                onChange={(e): void =>
                  handleZoneNameInputChange(e.target.value)
                }
              />

              <Typography variant="subtitle1">
                Set up the camera position
              </Typography>

              <Box>
                <ControlTable
                  pan={pan}
                  tilt={tilt}
                  zoom={zoom}
                  ptDisabled={disabled}
                  validation={validationState}
                  showValidationAlert
                  handlePanInputChange={handlePanInputChange}
                  handleGoToPt={handleGoToPt}
                  handleTiltInputChange={handleTiltInputChange}
                  handleZoomInputChange={handleZoomInputChange}
                  movePtLeft={movePtLeft}
                  movePtRight={movePtRight}
                  movePtUp={movePtUp}
                  movePtDown={movePtDown}
                  hasZoom={device?.deviceData?.hasZoom}
                />
              </Box>

              <FocusControls
                focusMode={focusMode}
                focusDistance={focusDistance}
                focusRect={focusRect}
                onFocusModeChange={mode => {
                  setFocusMode(mode);

                  debouncedSendFocus(mode, focusRect, focusDistance);
                }}
                onFocusDistanceChange={val => {
                  setFocusDistance(val);

                  debouncedSendFocus(focusMode, focusRect, val);
                }}
                onFocusRectChange={rect => {
                  setFocusRect(rect);

                  debouncedSendFocus(focusMode, rect, focusDistance);
                }}
                disabled={disabled}
              />

              <Divider flexItem />
            </Stack>
          </Box>
          <Box>
            <Box sx={{ position: "relative" }}>
              <HLSStreamPlayer fixedHeight={DEFAULT_MEDIA_HEIGHT} />
              {focusMode === "auto" && (
                <FocusRectOverlay
                  rect={focusRect ?? DEFAULT_FOCUS_RECT}
                  onChange={rect => {
                    setFocusRect(rect);

                    debouncedSendFocus("auto", rect, focusDistance);
                  }}
                />
              )}
            </Box>
          </Box>
        </ResponsiveSplitContainer>
      </MainPaperWrapper>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "1em",
        }}
      >
        <StyledLoadingButton
          sx={{
            marginTop: "1.5em",
          }}
          loading={loading}
          startIcon={null}
          loadingPosition="start"
          variant="outlined"
          color="inherit"
          onClick={confirmCancel}
        >
          Cancel
        </StyledLoadingButton>
        <StyledLoadingButton
          sx={{
            marginTop: "1.5em",
          }}
          loading={loading}
          startIcon={null}
          loadingPosition="start"
          variant="contained"
          color="primary"
          onClick={saveZone}
        >
          Create zone
        </StyledLoadingButton>
      </Box>
    </>
  );
};

export default CreateZoneContent;
